import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid2,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { Form, Formik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import useNotify from '../../hooks/useNotify';
import CommonButton from '../../components/atom/CommonButton';
import PageWithTitle from '../../components/atom/PageWithTitle';
import BlockWithTitle from '../../components/atom/BlockWithTitle';
import CapacityReservationEditor from './CapacityReservationEditor';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { addTimeslot, getTimeslots } from '@openapi/timeslots/timeslots';
import { useGetTicketTypes } from '@openapi/ticket-types/ticket-types';
import { useGetArrangements } from '@openapi/shows/shows';
import { getDateKeyInTimezone, parseDateTimeLocal } from '../../helpers/formatDateTime';

const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Mon-Sun display order

// The backend answers duplicate timeslots with 409 Conflict, so detection
// does not depend on translated message text.
function isDuplicateTimeslotError(error) {
    return (error?.response?.status ?? error?.status) === 409;
}

function generateTimeslots(startDate, endDate, days, time) {
    if (!startDate || !endDate || !days.length || !time) return [];
    const [sy, sm, sd] = startDate.split('-').map(Number);
    const [ey, em, ed] = endDate.split('-').map(Number);
    if (endDate < startDate) return [];

    const dates = [];
    const cur = new Date(Date.UTC(sy, sm - 1, sd));
    const endDt = new Date(Date.UTC(ey, em - 1, ed));
    while (cur <= endDt) {
        if (days.includes(cur.getUTCDay())) {
            const dateKey = [
                cur.getUTCFullYear(),
                String(cur.getUTCMonth() + 1).padStart(2, '0'),
                String(cur.getUTCDate()).padStart(2, '0'),
            ].join('-');
            dates.push({
                dateKey,
                startTime: parseDateTimeLocal(`${dateKey}T${time}`),
            });
        }
        cur.setUTCDate(cur.getUTCDate() + 1);
    }
    return dates;
}

function BulkAddTimeslotsPage() {
    const { t } = useTranslation();
    const { showId } = useParams();
    const navigate = useNavigate();
    const { notifyError, notifySuccess } = useNotify();
    const [progress, setProgress] = useState({ completed: 0, total: 0 });

    const today = getDateKeyInTimezone(new Date());

    const { data: ticketTypes, isLoading: loadingTicketTypes } = useGetTicketTypes(
        { optionalId: showId, onlyShowInList: true, includeSeats: true },
        { query: { onError: notifyError } },
    );

    const { data: arrangementsData } = useGetArrangements(Number(showId), {
        query: { enabled: !!showId, onError: notifyError },
    });
    const arrangements = arrangementsData?.arrangements || [];

    const validateSchema = Yup.object().shape({
        days: Yup.array().of(Yup.number()).min(1, t('common.required')),
        time: Yup.string().required(t('common.required')),
        startDate: Yup.string().required(t('common.required')),
        endDate: Yup.string()
            .required(t('common.required'))
            .test('endAfterStart', t('timeslots.bulkAdd.endAfterStart'), function (endDate) {
                const { startDate } = this.parent;
                if (!startDate || !endDate) return true;
                return endDate >= startDate;
            }),
    });

    const onSubmit = async (values) => {
        const candidateTimeslots = generateTimeslots(
            values.startDate,
            values.endDate,
            values.days,
            values.time,
        );
        let created = 0;
        let skipped = 0;

        try {
            const existing = await getTimeslots({
                optionalId: showId,
                pageSize: 10000,
            });
            const existingStartTimes = new Set(
                (existing.timeslots || []).map((timeslot) =>
                    new Date(timeslot.startTime).getTime(),
                ),
            );
            const timeslots = candidateTimeslots.filter(
                (timeslot) => !existingStartTimes.has(new Date(timeslot.startTime).getTime()),
            );

            skipped = candidateTimeslots.length - timeslots.length;
            setProgress({ completed: 0, total: timeslots.length });

            for (let index = 0; index < timeslots.length; index += 1) {
                try {
                    await addTimeslot({
                        showId: parseInt(showId, 10),
                        startTime: timeslots[index].startTime,
                        isReviewEmailSuppressed: values.isReviewEmailSuppressed,
                        ticketTypeReservedCapacity: values.ticketTypeReservedCapacity,
                        activeArrangementId: values.activeArrangementId || null,
                    });
                    created += 1;
                } catch (error) {
                    if (!isDuplicateTimeslotError(error)) {
                        throw error;
                    }
                    skipped += 1;
                } finally {
                    setProgress({ completed: index + 1, total: timeslots.length });
                }
            }
            navigate(`/shows/${showId}/timeslots`);
            notifySuccess(
                t('timeslots.notifications.bulkAdded', {
                    created,
                    skipped,
                }),
            );
        } catch (error) {
            notifyError(error);
        }
    };

    if (loadingTicketTypes || !ticketTypes) return null;

    return (
        <Formik
            onSubmit={onSubmit}
            initialValues={{
                days: [],
                time: '15:00',
                startDate: today,
                endDate: '',
                isReviewEmailSuppressed: false,
                ticketTypeReservedCapacity: {},
                activeArrangementId: null,
            }}
            validationSchema={validateSchema}
        >
            {({ isSubmitting, values, setFieldValue, touched, errors }) => {
                const previewCount = generateTimeslots(
                    values.startDate,
                    values.endDate,
                    values.days,
                    values.time,
                ).length;
                const progressPercent = progress.total
                    ? Math.round((progress.completed / progress.total) * 100)
                    : 0;

                return (
                    <Form>
                        <PageWithTitle
                            title={t('timeslots.bulkAddName')}
                            headline={
                                <Grid2 display={'flex'} flexDirection={'row'} gap={2}>
                                    <Grid2>
                                        <CommonButton
                                            fullWidth
                                            onClick={() => navigate(`/shows/${showId}/timeslots`)}
                                            label={t('common.cancel')}
                                            buttonType="secondary"
                                        />
                                    </Grid2>
                                    <Grid2>
                                        <CommonButton
                                            fullWidth
                                            disabled={isSubmitting || previewCount === 0}
                                            type="submit"
                                            label={t('common.save')}
                                            buttonType="primary"
                                        />
                                    </Grid2>
                                </Grid2>
                            }
                        >
                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <BlockWithTitle title={t('timeslots.bulkAdd.title')}>
                                    <Grid2 size={{ xs: 12 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('timeslots.bulkAdd.daysLabel')}
                                        </Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                            <ToggleButtonGroup
                                                value={values.days}
                                                onChange={(_, newDays) => {
                                                    setFieldValue('days', newDays);
                                                }}
                                                aria-label={t('timeslots.bulkAdd.daysLabel')}
                                                size="small"
                                                sx={{ flexWrap: 'wrap', gap: 0.5 }}
                                            >
                                                {DAY_ORDER.map((day) => (
                                                    <ToggleButton key={day} value={day}>
                                                        {t(`timeslots.bulkAdd.days.${day}`)}
                                                    </ToggleButton>
                                                ))}
                                            </ToggleButtonGroup>
                                        </Box>
                                        {touched.days && errors.days && (
                                            <Typography variant="caption" color="error">
                                                {errors.days}
                                            </Typography>
                                        )}
                                    </Grid2>
                                    <Grid2 size={{ xs: 12 }}>
                                        <TextField
                                            fullWidth
                                            type="time"
                                            label={t('timeslots.bulkAdd.timeLabel')}
                                            value={values.time}
                                            onChange={(ev) =>
                                                setFieldValue('time', ev.target.value)
                                            }
                                            InputLabelProps={{ shrink: true }}
                                            error={touched.time && Boolean(errors.time)}
                                            helperText={touched.time ? errors.time : ''}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12 }}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label={t('timeslots.bulkAdd.startDateLabel')}
                                            value={values.startDate}
                                            inputProps={{ min: today }}
                                            onChange={(ev) =>
                                                setFieldValue('startDate', ev.target.value)
                                            }
                                            InputLabelProps={{ shrink: true }}
                                            error={touched.startDate && Boolean(errors.startDate)}
                                            helperText={touched.startDate ? errors.startDate : ''}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12 }}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label={t('timeslots.bulkAdd.endDateLabel')}
                                            value={values.endDate}
                                            inputProps={{ min: values.startDate || today }}
                                            onChange={(ev) =>
                                                setFieldValue('endDate', ev.target.value)
                                            }
                                            InputLabelProps={{ shrink: true }}
                                            error={touched.endDate && Boolean(errors.endDate)}
                                            helperText={touched.endDate ? errors.endDate : ''}
                                        />
                                    </Grid2>
                                    <Grid2
                                        size={{ xs: 12 }}
                                        sx={{ display: 'flex', justifyContent: 'flex-start' }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={Boolean(
                                                        values.isReviewEmailSuppressed,
                                                    )}
                                                    onChange={(ev) =>
                                                        setFieldValue(
                                                            'isReviewEmailSuppressed',
                                                            ev.target.checked,
                                                        )
                                                    }
                                                />
                                            }
                                            label={t('timeslots.columns.isReviewEmailSuppressed')}
                                        />
                                    </Grid2>
                                    {arrangements.length > 0 && (
                                        <Grid2 size={{ xs: 12 }}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>
                                                    {t('seats.activeArrangement')}
                                                </InputLabel>
                                                <Select
                                                    value={values.activeArrangementId ?? ''}
                                                    label={t('seats.activeArrangement')}
                                                    onChange={(ev) =>
                                                        setFieldValue(
                                                            'activeArrangementId',
                                                            ev.target.value || null,
                                                        )
                                                    }
                                                >
                                                    <MenuItem value="">
                                                        {t('seats.defaultArrangement')}
                                                    </MenuItem>
                                                    {arrangements.map((arr) => (
                                                        <MenuItem key={arr.id} value={arr.id}>
                                                            {arr.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid2>
                                    )}
                                    {previewCount > 0 && (
                                        <Grid2 size={{ xs: 12 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('timeslots.bulkAdd.preview', {
                                                    count: previewCount,
                                                })}
                                            </Typography>
                                        </Grid2>
                                    )}
                                    {isSubmitting && progress.total > 0 && (
                                        <Grid2 size={{ xs: 12 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={progressPercent}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{ mt: 1 }}
                                                color="text.secondary"
                                            >
                                                {t('timeslots.bulkAdd.progressLabel', {
                                                    completed: progress.completed,
                                                    total: progress.total,
                                                    percent: progressPercent,
                                                })}
                                            </Typography>
                                        </Grid2>
                                    )}
                                </BlockWithTitle>
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <CapacityReservationEditor
                                    ticketTypes={ticketTypes.ticketTypes}
                                    value={values.ticketTypeReservedCapacity}
                                    onChange={(v) => setFieldValue('ticketTypeReservedCapacity', v)}
                                />
                            </Grid2>
                        </PageWithTitle>
                    </Form>
                );
            }}
        </Formik>
    );
}

export default BulkAddTimeslotsPage;
